import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

const ImageSlider = () => {
  const { isDark } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState([]);

  // Hero slides — legacy slider-images/* first, then fresh 2026 photos served from uploads/.
  // Images that 404 are filtered out during preload, so missing ones are harmless.
  const sliderImages = [
    '/slider-images/slide1.jpg',
    '/slider-images/slide2.jpg',
    '/slider-images/slide3.jpg',
    '/slider-images/slide4.jpg',
    '/slider-images/slide5.jpg',
    '/slider-images/slide6.jpg',
    '/slider-images/slide7.jpg',
    '/uploads/gallery/morning-assembly-formation-56385f74.jpeg',
    '/uploads/gallery/amphitheatre-group-portrait-af02ce8e.jpeg',
    '/uploads/gallery/smart-classroom-teaching-session-35b2f14e.jpeg',
    '/uploads/gallery/student-felicitation-ceremony-ccf590bc.jpeg',
    '/uploads/gallery/sports-medal-winners-with-faculty-9f1f826e.jpeg'
  ];

  // Preload images for better performance with error handling
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = sliderImages.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ src, loaded: true });
          img.onerror = () => resolve({ src, loaded: false });
          img.src = src;
          // Set loading attribute for better performance
          img.loading = 'eager';
          img.decoding = 'async';
        });
      });

      try {
        const results = await Promise.all(imagePromises);
        const validImages = results.filter(result => result.loaded).map(result => result.src);
        setLoadedImages(validImages);
        setIsLoading(false);
      } catch (error) {
        console.error('Error preloading images:', error);
        setIsLoading(false);
      }
    };

    preloadImages();
  }, []);

  // Auto-advance slides with improved timing
  useEffect(() => {
    if (loadedImages.length === 0) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % loadedImages.length);
    }, 5000); // Increased to 5 seconds for better viewing

    return () => clearInterval(timer);
  }, [loadedImages.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % loadedImages.length);
  }, [loadedImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + loadedImages.length) % loadedImages.length);
  }, [loadedImages.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        prevSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Show loading state or nothing if no images
  if (isLoading || loadedImages.length === 0) {
    return null; // Don't render anything if images aren't ready
  }

  return (
    <section className={`py-16 ${isDark ? 'bg-dark-surface' : 'bg-gray-50'}`}>
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl md:text-4xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
            School Gallery
          </h2>
          <p className={`text-lg ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Discover the vibrant life at PM SHRI GGSSS FARMANA through our photo gallery
          </p>
        </div>

        <div className="relative h-96 md:h-[28rem] lg:h-[36rem] xl:h-[42rem] rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700">
          {/* Main Image Display */}
          <AnimatePresence mode="wait">
            <motion.img
              key={currentSlide}
              src={loadedImages[currentSlide]}
              alt={`School gallery slide ${currentSlide + 1}`}
              className="absolute inset-0 w-full h-full object-cover object-bottom"
              loading="eager"
              decoding="async"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              onError={(e) => {
                console.log('Image failed to load:', e.target.src);
              }}
            />
          </AnimatePresence>

          {/* Subtle overlay for better control visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />

          {/* Enhanced Navigation Arrows with better styling */}
          {loadedImages.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 md:left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/95 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-xl backdrop-blur-sm border border-white/50"
                aria-label="Previous slide"
              >
                <ChevronLeftIcon className="w-6 h-6 md:w-7 md:h-7" />
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/95 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-xl backdrop-blur-sm border border-white/50"
                aria-label="Next slide"
              >
                <ChevronRightIcon className="w-6 h-6 md:w-7 md:h-7" />
              </button>
            </>
          )}

          {/* Enhanced Slide Indicators */}
          {loadedImages.length > 1 && (
            <div className="absolute bottom-4 md:bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 md:space-x-3 bg-black/20 backdrop-blur-sm rounded-full px-4 py-2">
              {loadedImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-white scale-125 shadow-lg'
                      : 'bg-white/60 hover:bg-white/80 hover:scale-110'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Enhanced Slide Counter */}
          <div className="absolute top-4 md:top-6 right-4 md:right-6 bg-black/40 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-sm font-medium backdrop-blur-sm border border-white/10">
            {currentSlide + 1} / {loadedImages.length}
          </div>
        </div>
        
        {/* Gallery Info */}
        <div className="mt-8 text-center">
          <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
            Click the arrows or dots to navigate through our school gallery • Auto-advance every 5 seconds
          </p>
        </div>
      </div>
    </section>
  );
};

export default ImageSlider;